import { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAuthToken } from '../../../commom/authToken';
import {
  getEssayThemeVideoEmbed,
  getModalVideoEmbed,
  getResolutionEmbed,
  getResolutionEmbedByVimeo,
} from '../../../queries/resolutionEmbed';
import PlayerAprova from '../../atoms/PlayerAprova';

const fetchEmbed = {
  aws: getResolutionEmbed,
  vimeo: getResolutionEmbedByVimeo,
  essayTheme: getEssayThemeVideoEmbed,
  modalVideo: getModalVideoEmbed,
};

interface PlayerAwsResolutionProps {
  /**
   * `aws`: token é o id do vídeo no Player AWS. `vimeo`: token é o Vimeo ID e
   * o backend acha o vídeo correspondente no Player AWS. `essayTheme`: token é
   * o _id da videoaula do tema de redação. `modalVideo`: token é o Vimeo ID de
   * um vídeo de modal ("Como funciona").
   */
  source?: keyof typeof fetchEmbed;
  token: string;
  /** Player de reserva quando o vídeo não está no Player AWS (Vimeo). */
  fallback?: () => ReactNode;
  onFinish?: () => any;
  virtualFinishAt?: number;
  onVideoEnd?: () => void;
  onProgressSeconds?: (seconds: number) => void;
  /** Tamanho que a tela deu ao player (ex.: 'h-[48vh] w-full'), o mesmo do Vimeo. */
  className?: string;
  /** Nome do vídeo no iframe (leitor de tela). */
  title?: string;
  /** Aviso quando o vídeo não abre e não há Vimeo de reserva. */
  errorMessage?: string;
}

/**
 * Resolução em vídeo pelo Player AWS. O exercício traz só o id do vídeo; a URL
 * do iframe é temporária e vem do backend a cada abertura do player, por isso
 * a consulta não fica em cache.
 */
export default function PlayerAwsResolution({
  source = 'aws',
  token,
  fallback,
  onFinish,
  virtualFinishAt,
  onVideoEnd,
  onProgressSeconds,
  className,
  title = 'Resolução do exercício',
  errorMessage = 'Não foi possível carregar o vídeo da resolução.',
}: PlayerAwsResolutionProps) {
  // A caixa leva o tamanho pedido pela tela e o conteúdo a preenche. Sem ela o
  // tamanho dependia do invólucro do Player, onde o h-full vem depois do
  // h-[48vh] no CSS: no modal (sem altura) isso dava zero e o player sumia.
  const frame = (content: ReactNode) => (
    <div className={className || 'h-full w-full'}>{content}</div>
  );

  // O embed do Player AWS exige login na plataforma. Sem ele (gabarito do
  // ENEM 365, por exemplo), nem pergunta: toca o Vimeo, como antes.
  const signedIn = Boolean(getAuthToken());
  const { data, isPending, isError, refetch, isFetching } = useQuery({
    queryKey: ['exercise-resolution-embed', source, token],
    queryFn: () => fetchEmbed[source](token),
    enabled: Boolean(token) && signedIn,
    gcTime: 0,
    staleTime: 0,
    // Pelo Vimeo ID ou pela videoaula, a resposta "não está no Player AWS" é
    // esperada: sem nova tentativa, o Vimeo entra logo.
    retry: source === 'aws' ? 1 : false,
    refetchOnWindowFocus: false,
    // Uma URL nova recarregaria o iframe e o vídeo voltaria ao início.
    refetchOnReconnect: false,
  });

  if (!signedIn && fallback) {
    return <>{fallback()}</>;
  }

  if (!token || !signedIn) {
    return frame(
      <div className='flex h-full w-full items-center justify-center bg-black p-4 text-center'>
        <p className='text-mono-color-white text-sm'>
          Vídeo indisponível no momento.
        </p>
      </div>
    );
  }

  if (isPending || (isError && isFetching)) {
    return frame(
      <div
        className='h-full w-full animate-pulse bg-black'
        aria-busy
        aria-label='Carregando player'
      />
    );
  }

  if ((isError || !data?.embedUrl) && fallback) {
    return <>{fallback()}</>;
  }

  if (isError || !data?.embedUrl) {
    return frame(
      <div className='flex h-full w-full flex-col items-center justify-center gap-md bg-black p-4 text-center'>
        <p className='text-mono-color-white text-sm'>
          {errorMessage}
        </p>
        <button
          type='button'
          onClick={() => refetch()}
          className='rounded-pill border-hairline border-mono-color-white px-4 py-2 text-sm text-mono-color-white'
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  // Atalhos desligados: na página do exercício as teclas são das alternativas.
  return frame(
    <PlayerAprova
      embedUrl={data.embedUrl}
      title={title}
      className='!h-full'
      onFinish={onFinish}
      virtualFinishAt={virtualFinishAt}
      onVideoEnd={onVideoEnd}
      onProgressSeconds={onProgressSeconds}
      keyboardShortcuts={false}
    />
  );
}
